import { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/common/Forms";
import { api, errMsg } from "@/lib/api";

export function ProtocolChecks({ protocols, value, onChange, testid = "protocol" }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {protocols.map((p) => (
        <label key={p.id} className="flex items-center gap-3 bg-ivory-deep border border-warm/50 px-4 py-2.5 text-sm cursor-pointer">
          <Checkbox data-testid={`${testid}-checkbox-${p.id}`} checked={value.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
          <span className={p.active === false ? "text-warm-text line-through" : ""}>{p.name}</span>
        </label>
      ))}
    </div>
  );
}

export default function ApprovalPanel({ center, lists, onDone }) {
  const [type, setType] = useState("");
  const [protocols, setProtocols] = useState(center.declared_protocols || []);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const declared = new Set(center.declared_protocols || []);

  const approve = async () => {
    if (!type) return toast.error("Assegna una tipologia cliente prima di approvare");
    setBusy(true);
    try {
      const r = await api.post(`/admin/centers/${center.id}/approve`, { client_type_id: type, enabled_protocols: protocols });
      toast.success("Centro approvato. Abbiamo inviato l'email di benvenuto.");
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/centers/${center.id}/reject`, { reason });
      toast.success("Richiesta rifiutata. Il centro è stato avvisato via email.");
      setRejectOpen(false);
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="approval-panel" className="rounded-2xl border border-gold/50 bg-ivory-card shadow-[0_0_40px_-15px_rgba(212,175,55,0.5)] p-6 sm:p-8 mb-8">
      <p className="eyebrow text-gold">Richiesta da verificare</p>
      <h2 className="text-2xl font-normal mt-2 mb-6">Approva l'iscrizione</h2>
      <div className="space-y-6">
        <Field label="Tipologia cliente *">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="approval-client-type-select" className="bg-ivory-deep rounded-xl h-11 sm:max-w-sm"><SelectValue placeholder="Scegli la tipologia" /></SelectTrigger>
            <SelectContent>
              {lists["client-types"].filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Protocolli abilitati" hint={`Dichiarati dal centro: ${lists.protocols.filter((p) => declared.has(p.id)).map((p) => p.name).join(", ") || "nessuno"}`}>
          <ProtocolChecks protocols={lists.protocols.filter((p) => p.active || declared.has(p.id))} value={protocols} onChange={setProtocols} testid="approval-protocol" />
        </Field>
        <div className="flex flex-wrap gap-3">
          <button data-testid="approve-center-btn" disabled={busy} onClick={approve} className="btn-gold">Approva</button>
          {center.status === "pending" && <button data-testid="reject-center-btn" disabled={busy} onClick={() => setRejectOpen(true)} className="btn-ghost">Rifiuta</button>}
        </div>
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-ivory-card rounded-xl" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal">Rifiuta la richiesta</DialogTitle>
            <DialogDescription>Il centro riceverà un'email. La motivazione è facoltativa e verrà inclusa nel messaggio.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className="bg-ivory-deep rounded-xl" placeholder="Motivazione (facoltativa)" />
          <div className="flex justify-end gap-3">
            <button onClick={() => setRejectOpen(false)} className="btn-link">Annulla</button>
            <button data-testid="reject-confirm-btn" disabled={busy} onClick={reject} className="btn-ghost">Conferma rifiuto</button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
